import React from 'react';
import * as Sentry from '@sentry/react';

// P4 — Observabilidade (monitoramento de erros).
//
// Desligado por padrão: só ativa quando REACT_APP_SENTRY_DSN está
// definido no build. Sem DSN, initSentry() é no-op — nada é enviado.
//
// LGPD: sendDefaultPii=false + os scrubbers abaixo removem dados
// pessoais antes do envio. O Session Replay NÃO é ativado de propósito:
// gravaria a tela do usuário, que contém conteúdo de NC / dado pessoal.

// Headers sensíveis que nunca devem ir para o Sentry.
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-auth-token'];

function scrubEvent(event) {
  // Mascara headers de auth de qualquer request anexado ao evento.
  if (event.request && event.request.headers) {
    const headers = event.request.headers;
    Object.keys(headers).forEach((key) => {
      if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
        headers[key] = '[scrubbed]';
      }
    });
  }
  // sendDefaultPii já é false; reforço — nunca enviar e-mail/IP do usuário.
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }
  return event;
}

function scrubBreadcrumb(breadcrumb) {
  // Breadcrumbs de fetch/xhr não devem carregar corpo de request/response.
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    if (breadcrumb.data) {
      delete breadcrumb.data.request_body;
      delete breadcrumb.data.response_body;
    }
  }
  return breadcrumb;
}

/**
 * Resolve o `environment` do Sentry de forma ROBUSTA.
 * O build do CRA congela NODE_ENV='production' em qualquer deploy (inclusive
 * staging) — então, sem REACT_APP_SENTRY_ENV setado, o staging reportava como
 * 'production' e disparava alertas de "incidente de produção" falsos. Aqui:
 * env explícita > hostname (staging.* / localhost) > fallback NODE_ENV.
 */
function resolveSentryEnvironment() {
  const explicit = (process.env.REACT_APP_SENTRY_ENV || '').trim();
  if (explicit) return explicit;
  try {
    const h = (window.location && window.location.hostname || '').toLowerCase();
    if (h === 'localhost' || h.startsWith('127.') || h.endsWith('.local')) return 'development';
    if (h.startsWith('staging.') || h.includes('-staging')) return 'staging';
  } catch (_) { /* SSR/sem window → cai no fallback */ }
  return process.env.NODE_ENV === 'development' ? 'development' : 'production';
}

/**
 * Inicializa o Sentry se REACT_APP_SENTRY_DSN estiver definido no build.
 * Retorna true se ativou, false se ficou desligado (sem DSN).
 */
export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: resolveSentryEnvironment(),
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    // LGPD: nunca anexar PII automaticamente.
    sendDefaultPii: false,
    // Performance tracing desligado por padrão (custo/quota).
    // Ligável via REACT_APP_SENTRY_TRACES_SAMPLE_RATE (ex.: 0.1 = 10%).
    tracesSampleRate: Number(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0
    ),
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  });
  return true;
}

/** Fallback exibido quando um erro de render quebra a árvore React. */
function ErrorFallback() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        color: '#1a1a1a',
      }}
    >
      <h1 style={{ fontSize: '20px', marginBottom: '8px' }}>
        Algo deu errado
      </h1>
      <p style={{ color: '#666', marginBottom: '20px', maxWidth: '420px' }}>
        Ocorreu um erro inesperado. Tente recarregar a página. Se o
        problema persistir, entre em contato com o suporte.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          fontSize: '14px',
          cursor: 'pointer',
          border: 'none',
          borderRadius: '6px',
          background: '#2563eb',
          color: '#fff',
        }}
      >
        Recarregar
      </button>
    </div>
  );
}

/**
 * ErrorBoundary da aplicação. Funciona mesmo sem o Sentry inicializado —
 * captura erros de render e mostra o fallback (evita a tela branca). Com
 * DSN configurado, também reporta a exceção ao Sentry.
 */
export function AppErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
